import React, { useRef, useEffect } from 'react';
import { toast } from 'react-hot-toast';
import { InventoryMap, SupplierMap, ScanMode, ScannerState } from './types';
import { useScannerLogic } from '../hooks/useScannerLogic';
import { ModeSelector } from './scanner/ModeSelector';
import { ScanInput } from './scanner/ScanInput';
import { ProductCard } from './scanner/ProductCard';
import { CartView } from './scanner/CartView';
import { TransactionHistory } from './scanner/TransactionHistory';
import { WeightInputModal } from './WeightInputModal';
import { getTheme } from './scanner/themeUtils';
import { Box, Check, Coins, X, Loader2, Printer, User, Pause, CreditCard } from 'lucide-react';

interface ScannerInterfaceProps {
  inventoryData: InventoryMap;
  setInventoryData: React.Dispatch<React.SetStateAction<InventoryMap>>;
  supplierData: SupplierMap;
  setSupplierData: React.Dispatch<React.SetStateAction<SupplierMap>>;
  savedState: ScannerState;
  setSavedState: React.Dispatch<React.SetStateAction<ScannerState>>;
  allowedModes: ScanMode[];
  hasPosModule?: boolean;
  onCheckoutStart?: () => void;
  onCheckoutComplete?: () => void;
  currentTerminalName: string;
}

const TransactionNoteField = React.memo(function TransactionNoteField({
  value,
  onChange,
}: {
  value: string;
  onChange: (value: string) => void;
}) {
  return (
    <div className="bg-slate-50 dark:bg-gray-700/50 p-4 rounded-lg">
      <label className="block text-sm text-gray-600 dark:text-gray-400 mb-2">Megjegyzés</label>
      <textarea
        value={value}
        onChange={(e) => onChange(e.target.value)}
        placeholder="Opcionális megjegyzés (pl. sérült termék, vevő kérés...)"
        rows={3}
        className="w-full px-3 py-2 rounded-lg border border-primary bg-card text-gray-900 dark:text-white placeholder-gray-400 dark:placeholder-gray-500 focus:outline-none focus:ring-2 focus:ring-accent resize-none"
      />
    </div>
  );
});

const ScannerInterface: React.FC<ScannerInterfaceProps> = ({
  inventoryData,
  setInventoryData,
  supplierData,
  setSupplierData,
  savedState,
  setSavedState,
  allowedModes,
  hasPosModule = false,
  onCheckoutStart,
  onCheckoutComplete,
  currentTerminalName,
}) => {
  const {
    // State
    barcodeInput,
    lastScanned,
    newName,
    newPrice,
    newSellingPrice,
    newPurchaseNet,
    newPurchaseGross,
    newSellingNet,
    newSellingGross,
    newVatRate,
    newMinQuantity,
    newUnit,
    scanMode,
    transactionQty,
    isMuted,
    isRapidMode,
    outOfStockError,
    isProcessingCheckout,
    lastTransaction,
    isPrinting,
    isSuspendingCart,
    showSuspendModal,
    suspendNotes,
    transactionNote,
    nameSuggestions,
    showSuggestions,
    recentTransactions,
    recentScans,
    safeCart,
    isCheckoutModalOpen,
    checkoutStep,
    selectedPaymentType,
    discountPercent,
    identifiedSellerName,
    lastItemDetails,
    lastItemQty,
    isKnown,
    safeAllowedModes,

    // Refs
    mainInputRef,
    nameInputRef,
    qtyInputRef,

    // Actions
    updateSavedState,
    updateNewUnit,
    handleScan,
    handleKeyDownMain,
    handleQtyChange,
    handleSaveNewProduct,
    handleApprove,
    handleDiscard,
    handleGenerateInternalEan,
    handleNameChange,
    handleCheckout,
    handleSuspendCart,
    handlePrintReceipt,
    toggleMute,
    toggleRapidMode,
    setScanMode,
    setOutOfStockError,
    setShowSuspendModal,
    setSuspendNotes,
    setTransactionNote,
    setShowSuggestions,
    setLastTransaction,
    setNameSuggestions,
    returnFocusToMainInput,

    // Weight modal state and handlers
    showWeightModal,
    pendingWeighableItem,
    handleWeightConfirm,
    setShowWeightModal,

    // Transaction confirmation state and handlers
    pendingTransactions,
    showTransactionConfirmationModal,
    transactionUser,
    handleConfirmTransactions,
    setShowTransactionConfirmationModal,

    // Adjustment confirmation state and handlers
    pendingAdjustment,
    handleFinalizeAdjustment,
  } = useScannerLogic({
    inventoryData,
    setInventoryData,
    supplierData,
    setSupplierData,
    savedState,
    setSavedState,
    allowedModes,
    hasPosModule,
    onCheckoutStart,
    onCheckoutComplete,
    currentTerminalName,
  });

  const theme = getTheme(scanMode);

  // Timeout ref for blur handling (memory leak prevention)
  const blurTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  // Cleanup timeout on unmount to prevent memory leak
  useEffect(() => {
    return () => {
      if (blurTimeoutRef.current) {
        clearTimeout(blurTimeoutRef.current);
      }
    };
  }, []);

  // Handle input blur for aggressive focus management
  const handleInputBlur = (e: React.FocusEvent<HTMLInputElement>) => {
    const relatedTarget = e.relatedTarget as HTMLElement;
    if (
      !relatedTarget ||
      (relatedTarget.tagName !== 'BUTTON' &&
        relatedTarget.tagName !== 'INPUT' &&
        relatedTarget.tagName !== 'TEXTAREA')
    ) {
      // Clear any pending blur timeout to prevent memory leak
      if (blurTimeoutRef.current) {
        clearTimeout(blurTimeoutRef.current);
      }
      blurTimeoutRef.current = setTimeout(() => {
        if (mainInputRef.current) {
          mainInputRef.current.focus();
        }
        blurTimeoutRef.current = null;
      }, 50);
    }
  };

  // Handle suggestion selection
  const handleSuggestionSelect = (suggestion: string) => {
    updateSavedState({ newName: suggestion });
    setNameSuggestions([]);
    setShowSuggestions(false);
    nameInputRef.current?.focus();
  };

  // Handle cart item removal
  const handleRemoveCartItem = (index: number) => {
    const item = safeCart[index];
    updateSavedState({
      cart: safeCart.filter(
        (_item: { ean: string; name: string; price: number; quantity: number }, idx: number) =>
          idx !== index
      ),
    });
    toast.success(`${item?.name || 'Tétel'} eltávolítva`);
  };

  // Handle mode change with cart protection
  const handleModeChange = (newMode: ScanMode) => {
    // If cart is empty, switch mode immediately
    if (safeCart.length === 0) {
      setScanMode(newMode);
      return;
    }

    // If cart has items, ask for confirmation
    const modeLabels: Record<ScanMode, string> = {
      COUNT: 'Leltár',
      SALE: 'Kassza',
      RETURN: 'Visszáru',
      SCRAP: 'Selejt',
      INTERNAL: 'Saját',
    };

    const currentModeLabel = modeLabels[scanMode] || scanMode;
    const newModeLabel = modeLabels[newMode] || newMode;

    const confirmed = window.confirm(
      `A módváltással (${currentModeLabel} → ${newModeLabel}) a jelenlegi kosár tartalma törlődik.\n\nFolytatja?`
    );

    if (confirmed) {
      // Clear cart and switch mode
      updateSavedState({ cart: [] });
      setScanMode(newMode);
      toast.success(`Módváltás: ${newModeLabel}`);
    }
  };

  return (
    <div className="max-w-3xl mx-auto p-4 md:p-6 space-y-6">
      {/* Mode Selector */}
      <ModeSelector
        allowedModes={safeAllowedModes as ScanMode[]}
        currentMode={scanMode}
        onModeChange={handleModeChange}
      />

      {/* Visual Feedback Banner */}
      {scanMode !== 'COUNT' && (
        <div
          className={`p-3 mb-4 rounded-lg text-center font-bold text-white animate-in fade-in flex items-center justify-center gap-2 ${theme.bg}`}
        >
          {scanMode === 'SALE'
            ? 'KASSZA MÓD: ÉRTÉKESÍTÉS (-)'
            : scanMode === 'SCRAP'
              ? 'SELEJTEZÉS MÓD (-)'
              : scanMode === 'INTERNAL'
                ? 'SAJÁT FELHASZNÁLÁS (-)'
                : 'VISSZÁRU MÓD (+)'}
        </div>
      )}

      {/* Out of Stock Warning */}
      {outOfStockError && (
        <div className="rounded-xl shadow-2xl border-l-8 overflow-hidden animate-in zoom-in duration-200 bg-red-50 dark:bg-red-900/20 border-red-500 dark:border-red-400">
          <div className="p-6">
            <div className="flex flex-col items-center text-center gap-4">
              <div className="flex items-center gap-3">
                <div className="bg-red-600 dark:bg-red-500 text-white rounded-full p-3">
                  <Box size={32} />
                </div>
                <div>
                  <h3 className="text-2xl font-bold text-red-800 dark:text-red-200 uppercase tracking-wider">
                    NINCS KÉSZLETEN!
                  </h3>
                  <p className="text-lg font-semibold text-red-700 dark:text-red-300 mt-1">
                    {outOfStockError.name}
                  </p>
                  <p className="text-sm text-red-600 dark:text-red-400 font-mono mt-1">
                    {outOfStockError.ean}
                  </p>
                </div>
              </div>
              <div className="w-full bg-red-100 dark:bg-red-900/40 rounded-lg p-4 border border-red-200 dark:border-red-800">
                <p className="text-red-800 dark:text-red-200 font-bold">
                  A termék készlete 0 db. Nem lehet kivonni!
                </p>
              </div>
              <button
                onClick={() => {
                  setOutOfStockError(null);
                  updateSavedState({ lastScanned: null });
                  returnFocusToMainInput();
                }}
                className="w-full bg-red-600 hover:bg-red-700 text-white font-bold py-3 px-6 rounded-lg transition shadow-md active:scale-95"
              >
                Bezárás
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Main Scanner Input */}
      <ScanInput
        barcodeInput={barcodeInput}
        scanMode={scanMode}
        isRapidMode={isRapidMode}
        isMuted={isMuted}
        mainInputRef={mainInputRef}
        onInputChange={(val) => updateSavedState({ barcodeInput: val })}
        onKeyDown={handleKeyDownMain}
        onScan={() => handleScan(false)}
        onToggleRapidMode={toggleRapidMode}
        onToggleMute={toggleMute}
        onGenerateInternalEan={handleGenerateInternalEan}
        onBlur={handleInputBlur}
      />

      {/* Product Card - Always show if lastScanned is set, regardless of scanMode */}
      {lastScanned && !outOfStockError && (
        <ProductCard
          lastScanned={lastScanned}
          isKnown={isKnown}
          lastItemDetails={lastItemDetails}
          lastItemQty={lastItemQty}
          scanMode={scanMode}
          transactionQty={transactionQty}
          newName={newName}
          newPrice={newPrice}
          newSellingPrice={newSellingPrice}
          newPurchaseNet={newPurchaseNet}
          newPurchaseGross={newPurchaseGross}
          newSellingNet={newSellingNet}
          newSellingGross={newSellingGross}
          newVatRate={newVatRate}
          newMinQuantity={newMinQuantity}
          newIsWeighable={savedState.newIsWeighable ?? false}
          newUnitWeight={savedState.newUnitWeight ?? ''}
          newUnit={newUnit}
          nameSuggestions={nameSuggestions}
          showSuggestions={showSuggestions}
          nameInputRef={nameInputRef}
          qtyInputRef={qtyInputRef}
          onNameChange={handleNameChange}
          onPriceChange={(val) => updateSavedState({ newPrice: val })}
          onSellingPriceChange={(val) => updateSavedState({ newSellingPrice: val })}
          onPurchaseNetChange={(val) => updateSavedState({ newPurchaseNet: val })}
          onPurchaseGrossChange={(val) => updateSavedState({ newPurchaseGross: val })}
          onSellingNetChange={(val) => updateSavedState({ newSellingNet: val })}
          onSellingGrossChange={(val) => updateSavedState({ newSellingGross: val })}
          onVatRateChange={(val) => updateSavedState({ newVatRate: val })}
          onMinQuantityChange={(val) => updateSavedState({ newMinQuantity: val })}
          onIsWeighableChange={(val) => updateSavedState({ newIsWeighable: val })}
          onUnitWeightChange={(val) => updateSavedState({ newUnitWeight: val })}
          onUnitChange={updateNewUnit}
          onQtyChange={handleQtyChange}
          onSave={handleSaveNewProduct}
          onApprove={handleApprove}
          onDiscard={handleDiscard}
          onGenerateInternalEan={handleGenerateInternalEan}
          onSuggestionSelect={handleSuggestionSelect}
        />
      )}

      {/* Shopping Cart View - Show in all modes with POS module if cart has items */}
      {hasPosModule && safeCart.length > 0 && (
        <CartView
          cart={safeCart}
          scanMode={scanMode}
          isSuspendingCart={isSuspendingCart}
          onCheckout={() => {
            updateSavedState({
              isCheckoutModalOpen: true,
              checkoutStep: 'SELECT_TYPE',
            });
          }}
          onRemoveItem={handleRemoveCartItem}
          onSuspend={() => setShowSuspendModal(true)}
        />
      )}

      {/* Transaction History - Hide in SALE mode */}
      {scanMode !== 'SALE' && (
        <TransactionHistory
          recentTransactions={recentTransactions}
          recentScans={recentScans}
          supplierData={supplierData}
        />
      )}

      {/* Checkout Modal */}
      {isCheckoutModalOpen && hasPosModule && (
        <div className="fixed inset-0 bg-black/50 dark:bg-black/70 flex items-center justify-center z-50 p-4 backdrop-blur-sm">
          <div className="bg-card rounded-xl shadow-2xl w-full max-w-md overflow-hidden animate-in fade-in zoom-in duration-200">
            {checkoutStep === 'SELECT_TYPE' ? (
              <>
                {(() => {
                  // Same header logic as kassza/visszáru: RETURN orange, rest primary
                  const headerConfig =
                    scanMode === 'RETURN'
                      ? {
                          color: 'bg-orange-600 dark:bg-orange-500',
                          hover: 'hover:bg-orange-700 dark:hover:bg-orange-600',
                          title: 'Visszáru Fizetési Mód',
                        }
                      : {
                          color: 'bg-primary',
                          hover: 'hover:bg-primary/90',
                          title: 'Fizetési Mód Választása',
                        };

                  return (
                    <div
                      className={`${headerConfig.color} p-4 flex justify-between items-center text-white`}
                    >
                      <h3 className="font-bold text-xl flex items-center gap-2">
                        <Coins size={24} />
                        {headerConfig.title}
                      </h3>
                      <button
                        onClick={() => {
                          updateSavedState({
                            isCheckoutModalOpen: false,
                            checkoutStep: 'SELECT_TYPE',
                            discountPercent: 0,
                          });
                        }}
                        className={`${headerConfig.hover} p-1 rounded`}
                        aria-label="Bezárás"
                      >
                        <X size={24} />
                      </button>
                    </div>
                  );
                })()}

                {/* Same flow for all modes (SALE, RETURN, SCRAP, INTERNAL): three payment options */}
                <div className="p-6 space-y-4">
                  <div className="text-center mb-6">
                    <p className="text-gray-600 dark:text-gray-300 text-lg">
                      Válassz fizetési módot
                    </p>
                  </div>

                  <div className="grid grid-cols-1 gap-4">
                    <button
                      onClick={() => {
                        updateSavedState({
                          selectedPaymentType: 'CASH',
                          checkoutStep: 'CONFIRM',
                        });
                      }}
                      className="w-full bg-primary hover:bg-primary/90 text-white py-6 rounded-xl font-bold text-xl transition shadow-lg hover:shadow-xl flex flex-col items-center gap-2"
                    >
                      <Coins size={32} />
                      KÉSZPÉNZ
                    </button>

                    <button
                      onClick={() => {
                        updateSavedState({
                          selectedPaymentType: 'INVOICE',
                          checkoutStep: 'CONFIRM',
                        });
                      }}
                      className="w-full bg-primary hover:bg-primary/90 text-white py-6 rounded-xl font-bold text-xl transition shadow-lg hover:shadow-xl flex flex-col items-center gap-2"
                    >
                      <User size={18} />
                      SZÁMLÁS
                    </button>

                    <button
                      onClick={() => {
                        updateSavedState({
                          selectedPaymentType: 'CARD',
                          checkoutStep: 'CONFIRM',
                        });
                      }}
                      className="w-full bg-purple-600 hover:bg-purple-700 text-white py-6 rounded-xl font-bold text-xl transition shadow-lg hover:shadow-xl flex flex-col items-center gap-2"
                    >
                      <CreditCard size={32} />
                      BANKKÁRTYA
                    </button>
                  </div>
                </div>
              </>
            ) : (
              <>
                {(() => {
                  // Determine modal theme based on scanMode
                  const modeConfig = pendingAdjustment
                    ? {
                        mode: pendingAdjustment.mode,
                        title:
                          pendingAdjustment.mode === 'RETURN'
                            ? 'VISSZÁRU'
                            : pendingAdjustment.mode === 'SCRAP'
                              ? 'SELEJTEZÉS'
                              : 'SAJÁT FELHASZNÁLÁS',
                        headerColor:
                          pendingAdjustment.mode === 'RETURN'
                            ? 'bg-orange-600 dark:bg-orange-500'
                            : pendingAdjustment.mode === 'SCRAP'
                              ? 'bg-red-600 dark:bg-red-500'
                              : 'bg-primary',
                        headerHover:
                          pendingAdjustment.mode === 'RETURN'
                            ? 'hover:bg-orange-700 dark:hover:bg-orange-600'
                            : pendingAdjustment.mode === 'SCRAP'
                              ? 'hover:bg-red-700 dark:hover:bg-red-600'
                              : 'hover:bg-primary/90',
                      }
                    : {
                        mode: scanMode,
                        title:
                          scanMode === 'SALE'
                            ? 'Eladás Megerősítése'
                            : scanMode === 'RETURN'
                              ? 'Visszáru Jóváhagyása'
                              : scanMode === 'SCRAP'
                                ? 'Selejtezés Véglegesítése'
                                : scanMode === 'INTERNAL'
                                  ? 'Saját Felhasználás'
                                  : 'Megerősítés',
                        headerColor:
                          scanMode === 'SALE'
                            ? 'bg-primary'
                            : scanMode === 'RETURN'
                              ? 'bg-orange-600 dark:bg-orange-500'
                              : scanMode === 'SCRAP'
                                ? 'bg-red-600 dark:bg-red-500'
                                : scanMode === 'INTERNAL'
                                  ? 'bg-primary'
                                  : 'bg-primary',
                        headerHover:
                          scanMode === 'SALE'
                            ? 'hover:bg-primary/90'
                            : scanMode === 'RETURN'
                              ? 'hover:bg-orange-700 dark:hover:bg-orange-600'
                              : scanMode === 'SCRAP'
                                ? 'hover:bg-red-700 dark:hover:bg-red-600'
                                : scanMode === 'INTERNAL'
                                  ? 'hover:bg-primary/90'
                                  : 'hover:bg-primary/90',
                      };

                  return (
                    <div
                      className={`${modeConfig.headerColor} p-4 flex justify-between items-center text-white`}
                    >
                      <h3 className="font-bold text-xl flex items-center gap-2">
                        <Check size={24} />
                        {pendingAdjustment
                          ? `${modeConfig.title} - Módosítás megerősítése`
                          : modeConfig.title}
                      </h3>
                      <button
                        onClick={() => {
                          if (pendingAdjustment) {
                            updateSavedState({
                              pendingAdjustment: null,
                              isCheckoutModalOpen: false,
                              checkoutStep: 'SELECT_TYPE',
                              discountPercent: 0,
                            });
                          } else {
                            updateSavedState({ checkoutStep: 'SELECT_TYPE', discountPercent: 0 });
                          }
                        }}
                        className={`${modeConfig.headerHover} p-1 rounded`}
                        aria-label="Vissza"
                      >
                        <X size={24} />
                      </button>
                    </div>
                  );
                })()}

                <div className="p-6 space-y-4">
                  {pendingAdjustment ? (
                    <>
                      <div className="space-y-3">
                        <div className="bg-slate-50 dark:bg-gray-700/50 p-4 rounded-lg">
                          <p className="text-sm text-gray-600 dark:text-gray-400 mb-1">Termék:</p>
                          <p className="font-bold text-gray-900 dark:text-white text-lg">
                            {pendingAdjustment.name}
                          </p>
                        </div>

                        <div className="bg-slate-50 dark:bg-gray-700/50 p-4 rounded-lg">
                          <p className="text-sm text-gray-600 dark:text-gray-400 mb-1">
                            Mennyiség:
                          </p>
                          <p className="font-bold text-gray-900 dark:text-white text-lg">
                            {pendingAdjustment.quantity} {pendingAdjustment.unit}
                          </p>
                        </div>

                        <div className="bg-slate-50 dark:bg-gray-700/50 p-4 rounded-lg">
                          <p className="text-sm text-gray-600 dark:text-gray-400 mb-1">
                            Azonosító:
                          </p>
                          <p className="font-bold text-gray-900 dark:text-white text-lg">
                            {identifiedSellerName ||
                              'Szkenneld be az azonosító kártyád a véglegesítéshez!'}
                          </p>
                        </div>
                      </div>

                      <TransactionNoteField value={transactionNote} onChange={setTransactionNote} />

                      <div className="pt-4 flex gap-3">
                        <button
                          onClick={() => {
                            updateSavedState({
                              pendingAdjustment: null,
                              isCheckoutModalOpen: false,
                              checkoutStep: 'SELECT_TYPE',
                              discountPercent: 0,
                            });
                          }}
                          className="flex-1 py-3 text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-lg transition font-bold"
                        >
                          Mégse
                        </button>
                        <button
                          onClick={handleFinalizeAdjustment}
                          disabled={isProcessingCheckout || !identifiedSellerName}
                          className="flex-1 py-3 bg-primary text-white rounded-lg hover:bg-primary/90 font-bold transition shadow-md disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2"
                        >
                          {isProcessingCheckout ? (
                            <>
                              <Loader2 className="animate-spin" size={18} />
                              Feldolgozás...
                            </>
                          ) : (
                            'OK - VÉGLEGESÍTÉS'
                          )}
                        </button>
                      </div>
                    </>
                  ) : (
                    <>
                      <div className="space-y-3">
                        <div className="bg-slate-50 dark:bg-gray-700/50 p-4 rounded-lg">
                          <p className="text-sm text-gray-600 dark:text-gray-400 mb-1">Eladó:</p>
                          <p className="font-bold text-gray-900 dark:text-white text-lg">
                            {identifiedSellerName || 'Szkenneld be az eladói kártyát!'}
                          </p>
                        </div>

                        {(() => {
                          const subtotal = safeCart.reduce(
                            (sum: number, item: { price: number; quantity: number }) =>
                              sum + item.price * item.quantity,
                            0
                          );
                          const discountAmt =
                            discountPercent > 0 ? Math.round(subtotal * discountPercent) / 100 : 0;
                          const finalTotal = subtotal - discountAmt;
                          const hasDiscount = discountPercent > 0;

                          return (
                            <>
                              <div className="bg-slate-50 dark:bg-gray-700/50 p-4 rounded-lg">
                                <p className="text-sm text-gray-600 dark:text-gray-400 mb-1">
                                  {hasDiscount
                                    ? 'Eredeti ár:'
                                    : scanMode === 'SALE' || scanMode === 'RETURN'
                                      ? 'Végösszeg:'
                                      : scanMode === 'SCRAP'
                                        ? 'Selejt Érték:'
                                        : scanMode === 'INTERNAL'
                                          ? 'Felhasználás Értéke:'
                                          : 'Végösszeg:'}
                                </p>
                                <p
                                  className={`font-extrabold ${hasDiscount ? 'text-lg line-through text-gray-400 dark:text-gray-500' : 'text-2xl'} ${
                                    !hasDiscount &&
                                    (scanMode === 'SALE'
                                      ? 'text-primary'
                                      : scanMode === 'RETURN'
                                        ? 'text-orange-600 dark:text-orange-400'
                                        : scanMode === 'SCRAP'
                                          ? 'text-red-600 dark:text-red-400'
                                          : 'text-primary')
                                  }`}
                                >
                                  {subtotal.toLocaleString('hu-HU')} Ft
                                </p>
                              </div>

                              {scanMode === 'SALE' && (
                                <div className="bg-slate-50 dark:bg-gray-700/50 p-4 rounded-lg">
                                  <p className="text-sm text-gray-600 dark:text-gray-400 mb-1">
                                    Kedvezmény (%)
                                  </p>
                                  <div className="flex items-center gap-3">
                                    <input
                                      type="number"
                                      min="0"
                                      max="100"
                                      step="1"
                                      value={discountPercent || ''}
                                      onChange={(e) => {
                                        const val = Math.min(
                                          100,
                                          Math.max(0, parseFloat(e.target.value) || 0)
                                        );
                                        updateSavedState({ discountPercent: val });
                                      }}
                                      onFocus={(e) => e.target.select()}
                                      placeholder="0"
                                      className="w-24 p-2 text-lg font-bold border border-slate-300 dark:border-gray-600 rounded focus:ring-2 focus:ring-primary outline-none bg-white dark:bg-gray-700 text-gray-900 dark:text-white text-center"
                                    />
                                    <span className="text-lg font-bold text-gray-600 dark:text-gray-400">
                                      %
                                    </span>
                                    {hasDiscount && (
                                      <span className="text-sm text-red-500 dark:text-red-400 font-medium">
                                        -{discountAmt.toLocaleString('hu-HU')} Ft
                                      </span>
                                    )}
                                  </div>
                                </div>
                              )}

                              {hasDiscount && (
                                <div className="bg-green-50 dark:bg-green-900/30 p-4 rounded-lg border border-green-200 dark:border-green-700">
                                  <p className="text-sm text-green-600 dark:text-green-400 mb-1">
                                    Kedvezményes végösszeg:
                                  </p>
                                  <p className="font-extrabold text-2xl text-green-600 dark:text-green-400">
                                    {finalTotal.toLocaleString('hu-HU')} Ft
                                  </p>
                                </div>
                              )}
                            </>
                          );
                        })()}

                        <div className="bg-slate-50 dark:bg-gray-700/50 p-4 rounded-lg">
                          <p className="text-sm text-gray-600 dark:text-gray-400 mb-1">Típus:</p>
                          <p className="font-bold text-gray-900 dark:text-white text-lg">
                            {selectedPaymentType === 'CASH'
                              ? 'Készpénz'
                              : selectedPaymentType === 'INVOICE'
                                ? 'Számlás'
                                : selectedPaymentType === 'CARD'
                                  ? 'Bankkártya'
                                  : 'Egyéb'}
                          </p>
                        </div>
                      </div>

                      <TransactionNoteField value={transactionNote} onChange={setTransactionNote} />

                      <div className="pt-4 flex gap-3">
                        <button
                          onClick={() => {
                            updateSavedState({ checkoutStep: 'SELECT_TYPE', discountPercent: 0 });
                          }}
                          className="flex-1 py-3 text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-lg transition font-bold"
                        >
                          Mégse
                        </button>
                        <button
                          onClick={handleCheckout}
                          disabled={
                            isProcessingCheckout || safeCart.length === 0 || !identifiedSellerName
                          }
                          className={`flex-1 py-3 text-white rounded-lg font-bold transition shadow-md disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2 ${
                            scanMode === 'SALE'
                              ? 'bg-primary hover:bg-primary/90'
                              : scanMode === 'RETURN'
                                ? 'bg-orange-600 dark:bg-orange-500 hover:bg-orange-700 dark:hover:bg-orange-600'
                                : scanMode === 'SCRAP'
                                  ? 'bg-red-600 dark:bg-red-500 hover:bg-red-700 dark:hover:bg-red-600'
                                  : scanMode === 'INTERNAL'
                                    ? 'bg-primary hover:bg-primary/90'
                                    : 'bg-primary hover:bg-primary/90'
                          }`}
                        >
                          {isProcessingCheckout ? (
                            <>
                              <Loader2 className="animate-spin" size={18} />
                              Feldolgozás...
                            </>
                          ) : (
                            'RÖGZÍTÉS'
                          )}
                        </button>
                      </div>
                    </>
                  )}
                </div>
              </>
            )}
          </div>
        </div>
      )}

      {/* Suspend Modal */}
      {showSuspendModal && (
        <div className="fixed inset-0 bg-black/50 dark:bg-black/70 flex items-center justify-center z-50 p-4 backdrop-blur-sm">
          <div className="bg-card rounded-xl shadow-2xl w-full max-w-md overflow-hidden animate-in fade-in zoom-in duration-200">
            <div className="bg-amber-600 dark:bg-amber-500 p-4 flex justify-between items-center text-white">
              <h3 className="font-bold text-xl flex items-center gap-2">
                <Pause size={24} />
                Kosár Parkolása
              </h3>
              <button
                onClick={() => {
                  setShowSuspendModal(false);
                  setSuspendNotes('');
                }}
                className="hover:bg-amber-700 dark:hover:bg-amber-600 p-1 rounded"
                aria-label="Bezárás"
              >
                <X size={24} />
              </button>
            </div>

            <div className="p-6 space-y-4">
              <div className="text-center mb-4">
                <p className="text-gray-600 dark:text-gray-300 text-lg mb-2">
                  Parkolod ezt a kosarat?
                </p>
                <p className="text-sm text-gray-500 dark:text-gray-400">
                  {safeCart.length} tétel •{' '}
                  {safeCart
                    .reduce(
                      (sum: number, item: { price: number; quantity: number }) =>
                        sum + item.price * item.quantity,
                      0
                    )
                    .toLocaleString('hu-HU')}{' '}
                  Ft
                </p>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                  Megjegyzés (opcionális)
                </label>
                <textarea
                  value={suspendNotes}
                  onChange={(e) => setSuspendNotes(e.target.value)}
                  placeholder="Pl: Pénztárca a kocsiban, visszajövök..."
                  className="w-full px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:ring-2 focus:ring-amber-500 focus:border-amber-500 resize-none"
                  rows={3}
                />
              </div>

              <div className="flex gap-3 pt-2">
                <button
                  onClick={() => {
                    setShowSuspendModal(false);
                    setSuspendNotes('');
                  }}
                  className="flex-1 py-3 bg-gray-200 dark:bg-gray-700 text-gray-800 dark:text-gray-200 rounded-lg font-semibold transition hover:bg-gray-300 dark:hover:bg-gray-600"
                >
                  Mégse
                </button>
                <button
                  onClick={handleSuspendCart}
                  disabled={isSuspendingCart || safeCart.length === 0}
                  className="flex-1 py-3 bg-amber-600 hover:bg-amber-700 disabled:bg-gray-400 disabled:cursor-not-allowed text-white rounded-lg font-semibold transition shadow-md flex items-center justify-center gap-2"
                >
                  {isSuspendingCart ? (
                    <>
                      <Loader2 className="animate-spin" size={18} />
                      Parkolás...
                    </>
                  ) : (
                    <>
                      <Pause size={18} />
                      Parkolás
                    </>
                  )}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Receipt Print Button */}
      {lastTransaction && (
        <div className="fixed bottom-4 right-4 z-40">
          <button
            onClick={handlePrintReceipt}
            disabled={isPrinting}
            className="bg-primary hover:bg-primary/90 disabled:bg-primary/50 disabled:cursor-not-allowed text-white px-6 py-3 rounded-lg shadow-lg font-bold transition flex items-center gap-2 animate-in fade-in slide-in-from-bottom-2"
            title="Nyugta nyomtatása"
          >
            {isPrinting ? (
              <>
                <Loader2 className="animate-spin" size={20} />
                Generálás...
              </>
            ) : (
              <>
                <Printer size={20} />
                Nyugta nyomtatása
              </>
            )}
          </button>
          <button
            onClick={() => setLastTransaction(null)}
            className="mt-2 w-full bg-gray-600 hover:bg-gray-700 text-white px-4 py-2 rounded-lg text-sm transition"
          >
            Bezárás
          </button>
        </div>
      )}

      {/* Transaction Confirmation Modal (RETURN/SCRAP/INTERNAL) */}
      {showTransactionConfirmationModal && (
        <div className="fixed inset-0 bg-black/50 dark:bg-black/70 flex items-center justify-center z-50 p-4 backdrop-blur-sm">
          <div className="bg-card rounded-xl shadow-2xl w-full max-w-md overflow-hidden animate-in fade-in zoom-in duration-200">
            <div
              className={`p-4 flex justify-between items-center text-white ${
                scanMode === 'RETURN'
                  ? 'bg-purple-600 dark:bg-purple-500'
                  : scanMode === 'SCRAP'
                    ? 'bg-red-600 dark:bg-red-500'
                    : 'bg-orange-600 dark:bg-orange-500'
              }`}
            >
              <h3 className="font-bold text-xl flex items-center gap-2">
                <User size={24} />
                {scanMode === 'RETURN'
                  ? 'Visszáru'
                  : scanMode === 'SCRAP'
                    ? 'Selejt'
                    : 'Saját felhasználás'}{' '}
                - Véglegesítés
              </h3>
              <button
                onClick={() => {
                  setShowTransactionConfirmationModal(false);
                }}
                className="hover:opacity-80 p-1 rounded"
                aria-label="Bezárás"
              >
                <X size={24} />
              </button>
            </div>

            <div className="p-6 space-y-4">
              <div className="bg-slate-50 dark:bg-gray-700/50 p-4 rounded-lg">
                <p className="text-sm text-gray-600 dark:text-gray-400 mb-1">Felhasználó:</p>
                <p className="font-bold text-gray-900 dark:text-white text-lg">
                  {transactionUser || 'Szkenneld be az eladói kártyát!'}
                </p>
                {!transactionUser && (
                  <p className="text-xs text-orange-600 dark:text-orange-400 mt-2 flex items-center gap-1">
                    <User size={14} />
                    Olvassd be a user kártyát a folytatáshoz
                  </p>
                )}
              </div>

              <div className="bg-slate-50 dark:bg-gray-700/50 p-4 rounded-lg">
                <p className="text-sm text-gray-600 dark:text-gray-400 mb-2">Tranzakciók:</p>
                <div className="space-y-2 max-h-60 overflow-y-auto">
                  {pendingTransactions.map((transaction, index) => (
                    <div key={`${transaction.ean}-${index}`} className="bg-card p-3 rounded">
                      <p className="font-semibold text-gray-900 dark:text-white">
                        {transaction.name}
                      </p>
                      <p className="text-sm text-gray-600 dark:text-gray-400">
                        Mennyiség: {transaction.quantity} {transaction.unit}
                      </p>
                    </div>
                  ))}
                </div>
                <p className="text-sm text-gray-600 dark:text-gray-400 mt-2">
                  Összesen: {pendingTransactions.length} tétel
                </p>
              </div>

              <TransactionNoteField value={transactionNote} onChange={setTransactionNote} />

              <div className="pt-4 flex gap-3">
                <button
                  onClick={() => {
                    setShowTransactionConfirmationModal(false);
                  }}
                  className="flex-1 py-3 text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-lg transition font-bold"
                >
                  Mégse
                </button>
                <button
                  onClick={handleConfirmTransactions}
                  disabled={
                    isProcessingCheckout || pendingTransactions.length === 0 || !transactionUser
                  }
                  className={`flex-1 py-3 text-white rounded-lg font-bold transition shadow-md disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2 ${
                    scanMode === 'RETURN'
                      ? 'bg-purple-600 hover:bg-purple-700'
                      : scanMode === 'SCRAP'
                        ? 'bg-red-600 hover:bg-red-700'
                        : 'bg-orange-600 hover:bg-orange-700'
                  }`}
                >
                  {isProcessingCheckout ? (
                    <>
                      <Loader2 className="animate-spin" size={18} />
                      Feldolgozás...
                    </>
                  ) : (
                    'OK - VÉGLEGESÍTÉS'
                  )}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Weight Input Modal */}
      <WeightInputModal
        isOpen={showWeightModal}
        onClose={() => setShowWeightModal(false)}
        onConfirm={handleWeightConfirm}
        productName={pendingWeighableItem?.product?.name || 'Ismeretlen termék'}
        unitWeight={pendingWeighableItem?.product?.unitWeight || 0}
      />
    </div>
  );
};

export default ScannerInterface;
